import CheckIcon from '@mui/icons-material/Check';
import { useState, useEffect, useContext, useRef } from 'react';
import scrollToTop from '@/utils/scroolToTop';
import {
  BodyLarge,
  BodyLargeEmphasized,
  BodyMedium,
  NavBarBackButton,
  SearchBox,
} from '@/design-system';
import colors from '@/theme/colors';
import styles from './Language.styles';
import languagesJson from './languages.json';
import UserPreferencesContext from '@/context/UserPreferencesContext';
import { AccessibleDivButton, AccessibleList, AccessibleListItem, LiveFeedback, PeachjarLoader } from '@/components';
import { useLiveFeedback } from '@/hooks';

export const Language: React.FC = () => {
  const {
    language: selectedLanguage,
    updateUserPreferences,
    loading,
  } = useContext(UserPreferencesContext);
  const [languageSearch, setLanguageSearch] = useState('');
  const [languages, setLanguages] = useState(Object.keys(languagesJson).map(language => language));
  const [feedbackMessage, setFeedbackMessage] = useLiveFeedback({ delay: 500 });
  const selectedButtonRef = useRef<HTMLDivElement>(null);
  const [focusSelected, setFocusSelected] = useState(false);

  useEffect(() => {
    if (focusSelected && selectedButtonRef.current) {
      selectedButtonRef.current.focus();
      setFocusSelected(false);
    }
  }, [focusSelected]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const filteredLanguages = Object.keys(languagesJson).filter(language =>
        language.toLowerCase().includes(languageSearch.toLowerCase())
      );
      setLanguages(filteredLanguages);
      setFeedbackMessage(`${filteredLanguages.length} languages found`);
    }, 500);

    return () => clearTimeout(timer);
  }, [languageSearch, setFeedbackMessage]);

  const onLanguageSearchChange = (value: string) => {
    setLanguageSearch(value);
  };

  const onSelectLanguage = async (newLanguage: string) => {
    const code = languagesJson[newLanguage];
    const language = newLanguage;
    await updateUserPreferences({
      language: {
        code,
        name: language,
      },
    });
    setLanguageSearch('');
    scrollToTop();
    setFocusSelected(true);
  };

  return (
    <>
      <NavBarBackButton trackId="languagePage">Language</NavBarBackButton>
      <PeachjarLoader loading={loading} />
      <main className={styles.container}>
        <div>
          <BodyMedium id="language-search-description" color={colors.slate}>
            Select the languages you prefer to view app content and receive email notifications in.
          </BodyMedium>
        </div>
      <LiveFeedback message={feedbackMessage} />
        <div className={styles.languageListContainer}>
          <SearchBox
            placeholder="Search for language"
            value={languageSearch}
            onChange={onLanguageSearchChange}
            showCancelButton
            aria-describedby="language-search-description"
          />
          <AccessibleList className={styles.languagesContainer}>
            {languageSearch.length === 0 && (
              <AccessibleListItem>
                <AccessibleDivButton
                  ref={selectedButtonRef}
                  className={styles.selectedLanguageContainer}
                  aria-pressed="true"
                  role="button"
                  tabIndex={0}
                >
                  <span className={styles.selectedLanguage}>
                    <BodyLargeEmphasized
                      as="span"
                      data-testid="languageLabel"
                      nonTranslate
                    >
                      {selectedLanguage.name}
                    </BodyLargeEmphasized>
                  </span>
                  <CheckIcon className={styles.checkIcon} aria-hidden="true" />
                </AccessibleDivButton>
              </AccessibleListItem>
            )}

            {languages.map(language => {
              if (language === selectedLanguage.name) return null;
              return (
                <AccessibleListItem key={language}>
                  <AccessibleDivButton
                    className={styles.languageItem}
                    onClick={async () => await onSelectLanguage(language)}
                    aria-label={`Select ${language} language`}
                    aria-pressed="false"
                  >
                    <div className={styles.language}>
                      <BodyLarge as="span" nonTranslate data-testid="languageLabel">
                        {language}
                      </BodyLarge>
                    </div>
                  </AccessibleDivButton>
                </AccessibleListItem>
              );
            })}
          </AccessibleList>
        </div>
      </main>
    </>
  );
};
